import React, { ReactElement } from 'react'
import { TableComplete, Header } from '@e3dc-react/shell/Elements/Table'
import { nullsLast } from '@e3dc-react/shell/Libs/SortAlgorithms'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'

import { useFiles } from 'src/Libs/installation-statement/hooks'
import { FileWithLatestStatus } from 'src/Libs/installation-statement/models'
import useInstallationFiles from 'src/Libs/documents/hooks/use-installation-files'
import PaperContainer from '../PaperContainer'
import { useTableColumnSize } from '../../hooks/use-table-column-size'
import { InstallationStatementModel } from 'src/Libs/installation-statement/models/statement'
import { formatInTimeZone, BERLIN_TIME_ZONE } from '@e3dc-react/shell/Libs/date'
import { FileLink } from 'src/Libs/installation-details/components'
import { useWarrantyInfo } from 'src/Libs/installation-details/hooks'

interface InformationProps {
  currentInstallationStatement?: InstallationStatementModel
  installationId?: string
}

/**
 * Installation statement images table
 */
const Images: React.FC<InformationProps> = ({ currentInstallationStatement, installationId }) => {
  const { t } = useCustomTranslation()
  const columnWidth = useTableColumnSize()
  const deviceLinkId = currentInstallationStatement?.deviceLinkId ?? 0
  const installationIdToUse = installationId ? Number.parseInt(installationId) : undefined
  const { warrantyInfo } = useWarrantyInfo(installationId, deviceLinkId, currentInstallationStatement?.id)
  const hasValidInstallationStatement = warrantyInfo?.isValid ?? !!currentInstallationStatement?.installationRequirements

  const [filesData] = useFiles({
    deviceLinkId,
    installationId: installationIdToUse,
    filter: 'mimeType=image*',
  })
  const { openFileInNewTab } = useInstallationFiles()

  const getFileLinkElement = (linkText: string, _: number, data?: FileWithLatestStatus): ReactElement => (
    <FileLink
      disabled={!(data?.isLatestVersion && hasValidInstallationStatement)}
      data={data}
      onClick={() => {
        openFileInNewTab(installationIdToUse, data!.id, data!.mimeType)
      }}
      text={linkText}
    />
  )

  const tableHeaders: Header<FileWithLatestStatus>[] = [
    {
      label: t('system-details.installation-statements.image-table.date'),
      value: 'createdAt',
      width: columnWidth,
      isSortable: true,
      sortFunction: nullsLast((a: string, b: string) => a.localeCompare(b)),
      valueRender: (value: string) => {
        return formatInTimeZone(new Date(value), BERLIN_TIME_ZONE, t('common.format.date-fns-date-time-without-seconds'))
      },
    },
    {
      value: 'isLatestVersion',
      label: t('system-details.installation-statements.image-table.status'),
      width: columnWidth,
      isSortable: true,
      sortFunction: nullsLast((a: boolean, b: boolean) => `${a}`.localeCompare(`${b}`)),
      valueRender: (value: boolean) =>
        value && hasValidInstallationStatement
          ? t('system-details.installation-statements.image-table.statuses.current')
          : t('system-details.installation-statements.image-table.statuses.deprecated'),
    },
    {
      label: t('system-details.installation-statements.image-table.file-name'),
      value: 'name',
      isSortable: true,
      sortFunction: nullsLast((a: string, b: string) => a.localeCompare(b)),
      valueRender: getFileLinkElement,
    },
  ]

  return (
    <PaperContainer title={t('system-details.installation-statements.image-table.title')}>
      <TableComplete
        noDataText={t('system-details.installation-statements.image-table.no-elements')}
        data={filesData}
        header={tableHeaders}
        initialSort={{
          name: 'createdAt',
          direction: 'desc',
        }}
        headerElevation={0}
        bodyElevation={0}
        footerElevation={0}
        coloredColumn
        disablePagination
      />
    </PaperContainer>
  )
}

export default Images
